import _ from 'underscore';
import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Text,
  Button,
  mergeClasses,
  Image,
} from '@fluentui/react-components';
import { PERMISSION_STATUS } from '@bingads-webui-campaign-react/linkedin-utils';

import { ComponyPageItem } from '../shared-components';
import { useLoadPermissionStatus, useResendPermissionRequest } from '../hooks';
import { usePermissionCheckStageV2Styles } from '../styles/permission-check-stage-V2-styles';
import PERMISSION_REJECTED_LOGO_SRC from '../../media/permission-rejected-logo.svg';
import PERMISSION_PENDING_LOGO_SRC from '../../media/permission-pending-logo.svg';
import PERMISSION_APPROVED_LOGO_SRC from '../../media/permission-approved-logo.svg';
import DIRECTING_ICON from '../../media/directing-icon.png';
import { COMPANY_PAGE_LINKING_ACTIONS } from '../reducer';

export const PermissionCheckStageV2 = (props) => {
  const {
    state,
    dispatch,
    onContinue,
    serverError,
    logger,
  } = props;

  const classes = usePermissionCheckStageV2Styles();
  const i18n = useContext(I18nContext);

  const { permissionStatus, connectedCompanyPage, isResendingPermissionRequest } = state;
  const {
    Name: companyPageName,
    LogoDownloadUrl: companyPageLogoSrc,
    Location: industryLocation,
    IndustryType: industryType,
    IsOrgUpdateAllowed: isOrgUpdateAllowed,
  } = connectedCompanyPage;

  useLoadPermissionStatus(state, dispatch, logger);
  useResendPermissionRequest(state, dispatch, logger);

  const onCheckStatus = useCallback(() => {
    dispatch({ type: COMPANY_PAGE_LINKING_ACTIONS.LOAD_PERMISSION_STATUS });
  }, [dispatch]);

  const onGoback = useCallback(() => {
    dispatch({
      type: COMPANY_PAGE_LINKING_ACTIONS.GO_BACK_AND_RESET_COMPANY_PAGE,
    });
  }, [dispatch]);

  const onResend = useCallback(() => {
    dispatch({ type: COMPANY_PAGE_LINKING_ACTIONS.RESEND_PERMISSION_REQUEST });
  }, [dispatch]);

  useEffect(() => {
    if (permissionStatus === PERMISSION_STATUS.APPROVED) {
      const timer = setTimeout(() => {
        onContinue();
      }, 2000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [permissionStatus, onContinue]);

  const renderPermissionStatusSession = useMemo(() => {
    let logoSrc;
    let title;
    let description;

    // eslint-disable-next-line default-case
    switch (state.permissionStatus) {
      case PERMISSION_STATUS.REJECTED:
        logoSrc = PERMISSION_REJECTED_LOGO_SRC;
        title = _TL_('Permission denied');
        description = _TL_('The company’s page administrator denied your request. You cannot proceed without permission.');
        break;
      case PERMISSION_STATUS.REQUESTED:
        logoSrc = PERMISSION_PENDING_LOGO_SRC;
        title = _TL_('Request pending approval');
        description = _TL_('The company’s page administrator hasn’t approved your request yet.');
        break;
      case PERMISSION_STATUS.APPROVED:
        logoSrc = PERMISSION_APPROVED_LOGO_SRC;
        title = _TL_('Permission approved');
        description = _TL_('Your request has been approved. Now you can create your campaign.');
    }

    return (
      <div className={mergeClasses('permission-status-session', classes.statusContainer)}>
        <Image src={logoSrc} className={classes.statusLogo} />
        <Text as="h1" className={mergeClasses('permission-status-title', classes.title)}>
          {i18n.getString(title)}
        </Text>
        <Text className={classes.description}>
          {i18n.getString(description)}
        </Text>
      </div>
    );
  }, [state, classes, i18n]);

  const renderActionButtons = useMemo(() => (
    <div className={classes.actionsContainer}>
      {permissionStatus === PERMISSION_STATUS.REQUESTED && (
      <Button
        appearance="primary"
        className="check-status-button"
        onClick={onCheckStatus}
        aria-label={i18n.getString(_TL_('Request pending approval, click here to check status.'))}
      >
        {i18n.getString(_TL_('Check status'))}
      </Button>
        )}
      {permissionStatus === PERMISSION_STATUS.REJECTED && isOrgUpdateAllowed && (
        <Button
          appearance="primary"
          className="go-back-button"
          onClick={onGoback}
          aria-label={i18n.getString(_TL_('Permission denied, click here to go back.'))}
        >
          {i18n.getString(_TL_('Go back'))}
        </Button>
        )}
      {permissionStatus === PERMISSION_STATUS.REJECTED && (
        <Button
          className="resend-button"
          onClick={onResend}
          disabled={isResendingPermissionRequest}
          aria-label={i18n.getString(_TL_('Permission denied, click here to resend request.'))}
        >
          {i18n.getString(_TL_('Resend request'))}
        </Button>
        )}
      {permissionStatus === PERMISSION_STATUS.APPROVED && (
        <Button
          icon={<Image src={DIRECTING_ICON} />}
          appearance="primary"
          className="step-next-button"
          aria-label={i18n.getString(_TL_('Your request has been approved.'))}
          disabled
        >
          {i18n.getString(_TL_('Loading'))}
        </Button>
        )}
    </div>
  ), [classes.actionsContainer, permissionStatus, onCheckStatus, i18n, isOrgUpdateAllowed, onGoback, onResend, isResendingPermissionRequest]);

  return (
    <div className={mergeClasses('permission-check-stage', classes.container)}>
      <div className={classes.contentContainer}>
        {renderPermissionStatusSession}
        <div className={classes.companyPageContainer}>
          <ComponyPageItem
            companyPageName={companyPageName}
            companyPageLogoSrc={companyPageLogoSrc}
            industryLocation={industryLocation}
            industryType={industryType}
          />
        </div>
      </div>
      {serverError}
      {renderActionButtons}
    </div>
  );
};

PermissionCheckStageV2.propTypes = {
  state: PropTypes.shape({
    permissionStatus: PropTypes.string,
    isResendingPermissionRequest: PropTypes.bool,
    connectedCompanyPage: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
      LogoDownloadUrl: PropTypes.string,
      Location: PropTypes.string,
      IndustryType: PropTypes.string,
      IsOrgUpdateAllowed: PropTypes.bool,
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  serverError: PropTypes.node,
  logger: PropTypes.shape({
    trace: PropTypes.func,
    error: PropTypes.func,
  }),
};

PermissionCheckStageV2.defaultProps = {
  serverError: null,
  logger: {
    trace: _.noop,
    error: _.noop,
  },
};
